/* eslint-disable */
import React, {  useEffect, useMemo, useState } from "react";
import { Loading, useRecordContext } from "react-admin";
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchCdpJson, cdpRetry } from '../../../helpers/cdp';
import { ensureOk } from '../../../helpers/http';

const CARE_DATA_PARSER_TRACKERS_URL = '/cdp/trackers';
// Relais serveur du backoffice (src/server/timeClockProxy.js, plan 018) : il
// exige la session care, puis ajoute la clé de service et l'auteur avant
// d'appeler la Lambda tracker-sync. Le navigateur ne voit ni l'une ni l'autre.
const TRACKER_SYNC_URL = '/time-clock/tracker-sync';

const useGetTrackers = (search) =>
  useQuery({
    queryKey: ['trackers'],
    queryFn: async () => {
      return fetchCdpJson(CARE_DATA_PARSER_TRACKERS_URL);
    },
    retry: cdpRetry,
    select: (data) => {
      const trackersList = Array.isArray(data) ? data : [];
      return search
        ? trackersList.filter(
            (t) =>
              (t.label || '').toLowerCase().includes(search) ||
              (t.siteLabel || '').toLowerCase().includes(search),
          )
        : trackersList;
    },
    initialData: [],
  });

// La Lambda refuse une clé de `site` présente à null (500 « Tracker
// validation failed ») : une valeur absente ou vide est omise.
const isFilled = (value) =>
  typeof value === 'string' ? value.trim() !== '' : value !== null && value !== undefined;

const toCoordinate = (value) =>
  isFilled(value) && Number.isFinite(Number(value)) ? Number(value) : undefined;

export const buildPayload = (tracker, record) => {
  const latitude = toCoordinate(record?.shipping_latitude);
  const longitude = toCoordinate(record?.shipping_longitude);
  return {
    tracker: tracker,
    site: {
      label: record?.name,
      accountId: record?.id,
      ...(latitude !== undefined ? { latitude } : {}),
      ...(longitude !== undefined ? { longitude } : {}),
      ...(isFilled(record?.shipping_street) ? { street: record.shipping_street } : {}),
      ...(isFilled(record?.shipping_postal_code) ? { zipCode: record.shipping_postal_code } : {}),
      ...(isFilled(record?.shipping_city) ? { city: record.shipping_city } : {}),
    },
  };
};

// Entrée dans le champ de recherche soumettrait le formulaire react-admin
// qui contient ce composant (enregistrement de toute la fiche).
const preventEnterSubmit = (e) => {
  if (e.key === 'Enter') e.preventDefault();
};

const updateTracker = async (tracker, record) => {
    return await fetch(TRACKER_SYNC_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload(tracker, record)),
    }).then(ensureOk);
};

const removeTracker = async (tracker) =>
  await fetch(TRACKER_SYNC_URL, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tracker,
      site: null,
    }),
  }).then(ensureOk);

export function TimeClockTrackerInput({editMode = true}) {
  const record = useRecordContext();
  const [search, setSearch] = useState('');

  const { data: trackers, isFetching, error, refetch } = useGetTrackers(
    search,
    { refetchOnWindowFocus: false },
  );

  const updateTrackerMutation = useMutation({
    mutationFn: (tracker) => updateTracker(tracker, record),
    onSuccess: () => {
      refetch();
      setMessage('✔ Tracker lié');
    },
    onError: (e) => {
      setMessage('❌ ' + e.message);
    },
  });

  const removeTrackerMutation = useMutation({
    mutationFn: (tracker) => removeTracker(tracker),
    onSuccess: () => {
      refetch();
      setMessage('✔ Tracker retiré');
    },
    onError: (e) => {
      setMessage('❌ ' + e.message);
    },
  });

  const [selected, setSelected] = useState('');
  const [message, setMessage] = useState('');

  if (isFetching) return <Loading />;
  if (error)
    return (
      <p style={{ color: '#d9534f' }}>
        {error.message || 'Erreur lors du chargement des badgeuses'}
      </p>
    );

  const currentAssociatedTracker = (trackers || []).find(
    (tracker) => tracker?.accountId === record?.id,
  );

  const siteName = record?.name;

  return (
    <div style={styles.container}>
      {editMode && (
        <h2 style={styles.title}>
          ⏱️ Badgeuse{siteName ? ` — ${siteName}` : ''}
        </h2>
      )}

      <div style={currentAssociatedTracker ? styles.boxOK : styles.boxWarn}>
        {currentAssociatedTracker ? (
          <>
            <strong>Tracker associé :</strong> <br />
            {currentAssociatedTracker.label}
            {currentAssociatedTracker.siteLabel && (
              <>
                <br />
                <small>({currentAssociatedTracker.siteLabel})</small>
              </>
            )}
          </>
        ) : (
          '⚠ Aucune badgeuse associée'
        )}
      </div>
      {editMode && (
        <>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={preventEnterSubmit}
            placeholder="Rechercher..."
            style={styles.input}
          />

          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            style={styles.select}
          >
            <option value="">-- Sélectionner --</option>
            {(trackers || []).map((t) => (
              <option key={t.label} value={t.label}>
                {t.label} {t.siteLabel ? `(${t.siteLabel})` : ''}
              </option>
            ))}
          </select>

          <div style={styles.actions}>
            <button
              type="button"
              style={styles.btnPrimary(updateTrackerMutation.isLoading)}
              disabled={!selected || updateTrackerMutation.isLoading}
              onClick={() =>
                updateTrackerMutation.mutate(
                  (trackers || []).find((t) => t.label === selected),
                )
              }
            >
              {updateTrackerMutation.isLoading ? '...' : 'Associer'}
            </button>

            <button
              type="button"
              style={styles.btnDanger(removeTrackerMutation.isLoading)}
              disabled={
                !currentAssociatedTracker || removeTrackerMutation.isLoading
              }
              onClick={() =>
                removeTrackerMutation.mutate(currentAssociatedTracker)
              }
            >
              {removeTrackerMutation.isLoading ? '...' : 'Retirer'}
            </button>
          </div>

          {message && (
            <p
              style={{
                ...styles.message,
                background: message.startsWith('❌') ? '#ffe5e5' : '#e6f9ec',
                color: message.startsWith('❌') ? '#d9534f' : '#2d8a4e',
              }}
            >
              {message}
            </p>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    marginTop: 20,
    fontFamily: "system-ui",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  title: { marginBottom: 16, fontSize: "1.6rem", color: "#222" },
  boxOK: {
    background: "#e6f9ec",
    padding: "14px 18px",
    borderRadius: 10,
    marginBottom: 12,
    fontSize: 16,
    width: "80%",
    maxWidth: 480,
    textAlign: "center",
  },
  boxWarn: {
    background: "#fff8e1",
    padding: "14px 18px",
    borderRadius: 10,
    marginBottom: 12,
    fontSize: 16,
    width: "80%",
    maxWidth: 480,
    textAlign: "center",
  },
  input: {
    width: "80%",
    maxWidth: 480,
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #bbb",
    marginBottom: 12,
    fontSize: 16,
  },
  select: {
    width: "80%",
    maxWidth: 480,
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #bbb",
    marginBottom: 16,
    fontSize: 16,
    background: "#fff",
  },
  actions: {
    display: "flex",
    gap: 16,
    width: "80%",
    maxWidth: 480,
    justifyContent: "center",
  },
  btnPrimary: (l) => ({
    flex: 1,
    padding: "12px 0",
    background: l ? "#aaa" : "#007bff",
    color: "white",
    border: "none",
    borderRadius: 8,
    cursor: l ? "not-allowed" : "pointer",
    fontSize: 16,
    fontWeight: 600,
  }),
  btnDanger: (l) => ({
    flex: 1,
    padding: "12px 0",
    background: l ? "#aaa" : "#dc3545",
    color: "white",
    border: "none",
    borderRadius: 8,
    cursor: l ? "not-allowed" : "pointer",
    fontSize: 16,
    fontWeight: 600,
  }),
  message: {
    marginTop: 16,
    padding: "10px 16px",
    borderRadius: 10,
    width: "80%",
    maxWidth: 480,
    fontSize: 16,
    textAlign: "center",
    fontWeight: 500,
  },
};
